import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, ArrowLeft, ChevronDown, Settings, Eye, Zap, Database, Trash2, Save, RefreshCw } from 'lucide-react';
import { MainLayout } from '../layout/MainLayout';
import apiClient from '../utils/apiClient';

// Custom hooks
const useProjectName = (projectId) => {
  const [projectName, setProjectName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjectName = async () => {
      if (!projectId) return;

      try {
        setIsLoading(true);
        setError(null);
        
        const data = await apiClient.get(`/api/projects/${projectId}`, {
          useOperational: true,
          showToast: false,
        });

        setProjectName(data.name);
      } catch (err) {
        console.error('Error fetching project name:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectName();
  }, [projectId]);

  return { projectName, isLoading, error };
};

const useConfigurations = (projectId) => {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConfigurations = useCallback(async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    try {
      const response = await apiClient.get('/api/config/sensor-configs', {
        useOperational: true,
        params: { project_id: projectId },
        showToast: false,
      });
      
      // Handle both array response and single object response
      const configsArray = Array.isArray(response) ? response : [response];
      setConfigs(configsArray);
      setError(null);
    } catch (err) {
      console.error('Error fetching configurations:', err);
      setError(err.message);
      setConfigs([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  return { configs, isLoading, error, refetch: fetchConfigurations };
};

const useFileUpload = (projectId, projectName) => {
  const [uploadStatus, setUploadStatus] = useState({});
  const [isUploading, setIsUploading] = useState({});

  const uploadFile = useCallback(async (file, fileType = 'window') => {
    if (!projectId || !projectName) {
      throw new Error('Project ID or Project Name is missing');
    }

    const validExtensions = ['.csv', '.xlsx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      throw new Error(`Please select a valid ${validExtensions.join(' or ')} file.`);
    }

    const uploadKey = fileType;
    setIsUploading(prev => ({ ...prev, [uploadKey]: true }));
    setUploadStatus(prev => ({ ...prev, [uploadKey]: 'Uploading file...' }));

    try {
      const destPath = fileType === 'window' 
        ? `raytracing/raytracing/Projects/${projectName}/DigitalTwin/Target_Surfaces/${file.name}`
        : `raytracing/raytracing/Projects/${projectName}/01_Input/GeospatialData/RoomData/${file.name}`;

      await apiClient.uploadFile('/api/files/upload-form', file, {
        destPath,
        overwrite: 'true',
      }, {
        useOperational: true,
        showToast: false,
      });

      setUploadStatus(prev => ({ 
        ...prev, 
        [uploadKey]: `File uploaded successfully: ${file.name}` 
      }));
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setUploadStatus(prev => ({ ...prev, [uploadKey]: '' }));
      }, 3000);

      return file.name;
    } catch (error) {
      setUploadStatus(prev => ({ 
        ...prev, 
        [uploadKey]: `Error: ${error.message}` 
      }));
      throw error;
    } finally {
      setIsUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  }, [projectId, projectName]);

  return { uploadFile, uploadStatus, isUploading };
};

const RaytracingConfiguration = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // Custom hooks
  const { projectName, isLoading: isLoadingProject } = useProjectName(projectId);
  const { configs, isLoading: isLoadingConfigs, refetch } = useConfigurations(projectId);
  const { uploadFile, uploadStatus, isUploading } = useFileUpload(projectId, projectName);

  // Form state with updated default values
  const [formData, setFormData] = useState({
    meshingMethod: 'Amount',
    distanceBetweenSensors: '',
    minimumDistanceFromSides: '',
    horizontal: '1',
    vertical: '1',
    sensorOffset: '0.15',
    surfaceOrientation: 'Yes',
    windowScheduleFile: null,
    roomDataFile: null,
    roomWindowClustering: 'No',
    higherLevelClustering: 'No',
    considerTerrain: 'No',
    considerVegetation: 'Yes',
    squareMeterPerSensor: '2.5',
    alpha: '0.51', // Updated default value
    marginPercent: '0', // Updated default value
    meshingDetail: '10.0',
    numberOfSkylines: '2',
    plotSkyline: 'Yes',
    storeSkyline: 'Yes',
    optimalCubeLength: '2.0',
    processingInterval: '15',
    clusterLevel1: 'WINDOW',
    clusterLevel2: 'ROOM', 
    clusterLevel3: 'FLOOR',
    clusterLevel4: 'BUILDING',
    clusterLevel5: 'NONE',
    importanceOfKeepingRatio: '0.51',
    minDistanceFromSidesPercent: '0'
  });

  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const loadConfiguration = useCallback((config) => {
    console.log('Loading configuration:', config);
    setSelectedConfigId(config.id);
    setFormData({
      meshingMethod: config.meshing_method || 'Amount',
      distanceBetweenSensors: config.distance_between_sensors?.toString() || '',
      minimumDistanceFromSides: config.minimum_distance_from_sides?.toString() || '',
      horizontal: config.horizontal?.toString() || '1',
      vertical: config.vertical?.toString() || '1',
      sensorOffset: config.sensor_offset_f?.toString() || '0.15',
      surfaceOrientation: config.surface_orientation ? 'Yes' : 'No',
      windowScheduleFile: null,
      roomDataFile: null,
      roomWindowClustering: config.room_window_clustering ? 'Yes' : 'No',
      higherLevelClustering: config.higher_level_clustering ? 'Yes' : 'No',
      considerTerrain: config.consider_terrain ? 'Yes' : 'No',
      considerVegetation: config.consider_vegetation ? 'Yes' : 'No',
      squareMeterPerSensor: config.square_meter_per_sensor?.toString() || '2.5',
      alpha: config.alpha?.toString() || '0.51', // Updated default value
      marginPercent: config.margin_percent?.toString() || '0', // Updated default value
      meshingDetail: config.meshing_detail?.toString() || '10.0',
      numberOfSkylines: config.number_of_skylines?.toString() || '2',
      plotSkyline: config.plot_skyline ? 'Yes' : 'No',
      storeSkyline: config.store_skyline ? 'Yes' : 'No',
      optimalCubeLength: config.optimal_cube_length?.toString() || '-1',
      processingInterval: config.processing_interval || '15',
      clusterLevel1: 'WINDOW',
      clusterLevel2: 'ROOM',
      clusterLevel3: 'FLOOR', 
      clusterLevel4: 'BUILDING',
      clusterLevel5: 'NONE',
      importanceOfKeepingRatio: '0.51',
      minDistanceFromSidesPercent: '0'
    });
  }, []);

  // Load first config when configs are available
  useEffect(() => {
    if (configs.length > 0 && !selectedConfigId) {
      console.log('Auto-loading first configuration:', configs[0]);
      loadConfiguration(configs[0]);
    }
  }, [configs, selectedConfigId, loadConfiguration]);

  const handleFileUpload = useCallback(async (event, fileType = 'window') => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('File upload triggered:', { fileType, fileName: file.name, projectId, projectName });

    try {
      const fileName = await uploadFile(file, fileType);
      console.log('File uploaded successfully:', fileName);
      
      setFormData(prev => ({
        ...prev,
        [fileType === 'window' ? 'windowScheduleFile' : 'roomDataFile']: fileName
      }));
    } catch (error) {
      console.error('File upload failed:', error);
    }
  }, [uploadFile, projectId, projectName]);

  const calculateClusterLevel = useCallback(() => {
    const levels = [
      formData.clusterLevel1,
      formData.clusterLevel2, 
      formData.clusterLevel3,
      formData.clusterLevel4,
      formData.clusterLevel5
    ];
    return levels.filter(level => level !== 'NONE').length;
  }, [formData]);

  const prepareApiData = useCallback(() => ({
    project_id: parseInt(projectId),
    processing_interval: formData.processingInterval,
    selected_environment: "https://api.apps.leaftech.eu/api/",
    meshing_method: formData.meshingMethod,
    distance_between_sensors: formData.distanceBetweenSensors ? parseFloat(formData.distanceBetweenSensors) : 1,
    minimum_distance_from_sides: formData.minimumDistanceFromSides ? parseFloat(formData.minimumDistanceFromSides) : null,
    horizontal: parseInt(formData.horizontal) || 1,
    vertical: parseInt(formData.vertical) || 1,
    surface_orientation: formData.surfaceOrientation === 'Yes',
    room_window_clustering: formData.roomWindowClustering === 'Yes',
    higher_level_clustering: formData.higherLevelClustering === 'Yes',
    sensor_offset_f: parseFloat(formData.sensorOffset) || 0.15,
    consider_terrain: formData.considerTerrain === 'Yes',
    consider_vegetation: formData.considerVegetation === 'Yes',
    square_meter_per_sensor: parseFloat(formData.squareMeterPerSensor) || 2.5,
    alpha: parseFloat(formData.alpha) || 0.51,
    margin_percent: parseFloat(formData.marginPercent) || 0,
    meshing_detail: parseFloat(formData.meshingDetail) || 0,
    number_of_skylines: parseInt(formData.numberOfSkylines) || 2,
    plot_skyline: formData.plotSkyline === 'Yes',
    store_skyline: formData.storeSkyline === 'Yes',
    setup_time_dict_cloud_done: false,
    optimal_cube_length: parseFloat(formData.optimalCubeLength) || -1,
    room_window_level_clustering: calculateClusterLevel()
  }), [projectId, formData, calculateClusterLevel]);

  const handleSave = useCallback(async () => {
    // Validation for required window schedule file
    if (formData.surfaceOrientation === 'Yes' && !formData.windowScheduleFile) {
      alert('Window Schedule File is required when Surface Orientation is enabled!');
      return;
    }

    setIsSaving(true);
    try {
      const apiData = prepareApiData();
      console.log('Saving configuration:', apiData);
      
      if (selectedConfigId) {
        // Update existing configuration
        await apiClient.put(`/api/config/sensor-configs/${selectedConfigId}`, apiData, {
          useOperational: true,
          showToast: false,
        });
        console.log('Configuration updated successfully');
      } else {
        // Create new configuration
        const result = await apiClient.post('/api/config/sensor-configs', apiData, {
          useOperational: true,
          showToast: false,
        });
        console.log('New configuration created:', result);
        setSelectedConfigId(result.id);
      }
      
      await refetch();
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert(`Error saving configuration: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  }, [selectedConfigId, prepareApiData, refetch, formData.surfaceOrientation, formData.windowScheduleFile]);

  const handleDelete = useCallback(async () => {
    if (!selectedConfigId || !window.confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    try {
      console.log('Deleting configuration with ID:', selectedConfigId);
      await apiClient.delete(`/api/config/sensor-configs/${selectedConfigId}`, {
        useOperational: true,
        showToast: false,
      });
      
      console.log('Configuration deleted successfully');
      setSelectedConfigId(null);
      
      // Reset form to default values
      setFormData({
        meshingMethod: 'Amount',
        distanceBetweenSensors: '',
        minimumDistanceFromSides: '',
        horizontal: '1',
        vertical: '1',
        sensorOffset: '0.15',
        surfaceOrientation: 'Yes',
        windowScheduleFile: null,
        roomDataFile: null,
        roomWindowClustering: 'No',
        higherLevelClustering: 'No',
        considerTerrain: 'No',
        considerVegetation: 'Yes',
        squareMeterPerSensor: '2.5',
        alpha: '0.51', // Updated default value
        marginPercent: '0', // Updated default value
        meshingDetail: '10.0',
        numberOfSkylines: '2',
        plotSkyline: 'Yes',
        storeSkyline: 'Yes',
        optimalCubeLength: '2.0',
        processingInterval: '15',
        clusterLevel1: 'WINDOW',
        clusterLevel2: 'ROOM', 
        clusterLevel3: 'FLOOR',
        clusterLevel4: 'BUILDING',
        clusterLevel5: 'NONE',
        importanceOfKeepingRatio: '0.51',
        minDistanceFromSidesPercent: '0'
      });
      
      await refetch();
      alert('Configuration deleted successfully!');
    } catch (error) {
      console.error('Error deleting configuration:', error);
      alert(`Error deleting configuration: ${error.message}`);
    }
  }, [selectedConfigId, refetch]);

  const createNewConfiguration = useCallback(() => {
    setSelectedConfigId(null);
    setFormData({
      meshingMethod: 'Amount',
      distanceBetweenSensors: '',
      minimumDistanceFromSides: '',
      horizontal: '1',
      vertical: '1',
      sensorOffset: '0.15',
      surfaceOrientation: 'Yes',
      windowScheduleFile: null,
      roomDataFile: null,
      roomWindowClustering: 'No',
      higherLevelClustering: 'No',
      considerTerrain: 'No',
      considerVegetation: 'Yes',
      squareMeterPerSensor: '2.5',
      alpha: '0.51', // Updated default value
      marginPercent: '0', // Updated default value
      meshingDetail: '10.0',
      numberOfSkylines: '2',
      plotSkyline: 'Yes',
      storeSkyline: 'Yes',
      optimalCubeLength: '2.0',
      processingInterval: '15',
      clusterLevel1: 'WINDOW',
      clusterLevel2: 'ROOM', 
      clusterLevel3: 'FLOOR',
      clusterLevel4: 'BUILDING',
      clusterLevel5: 'NONE',
      importanceOfKeepingRatio: '0.51',
      minDistanceFromSidesPercent: '0'
    });
  }, []);

  // Component helpers
  const SelectField = ({ label, value, onChange, options, className = "", icon }) => (
    <div className={`mb-6 ${className}`}>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">
        {icon && <span className="text-blue-600">{icon}</span>}
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full p-4 border-2 border-slate-200 rounded-xl appearance-none bg-white hover:border-blue-300 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md text-slate-700 font-medium"
        >
          {options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
          <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors duration-200" />
        </div>
      </div>
    </div>
  );

  const InputField = ({ label, value, onChange, type = "text", className = "", icon, step, placeholder }) => (
    <div className={`mb-6 ${className}`}>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">
        {icon && <span className="text-blue-600">{icon}</span>}
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        step={step}
        placeholder={placeholder}
        className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-300 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md text-slate-700 font-medium"
      />
    </div>
  );

  const FileUploadField = ({ label, fileName, onFileChange, fileType = 'window' }) => {
    const uploadKey = fileType;
    const currentUploadStatus = uploadStatus[uploadKey] || '';
    const currentIsUploading = isUploading[uploadKey] || false;

    const handleFileInputChange = (e) => {
      console.log('File input change triggered:', { fileType, files: e.target.files });
      onFileChange(e, fileType);
    };

    return (
      <div className="mb-6">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">
          <Database className="w-4 h-4 text-blue-600" />
          {label}
        </label>
        <div className="relative">
          <input
            type="file"
            onChange={handleFileInputChange}
            accept=".csv,.xlsx"
            className="hidden"
            id={`${fileType}-schedule-upload`}
            disabled={currentIsUploading}
          />
          <label
            htmlFor={`${fileType}-schedule-upload`}
            className={`group flex items-center justify-center gap-3 w-full p-6 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 ${
              currentIsUploading ? 'opacity-50 cursor-not-allowed' : ''
            } ${fileName ? 'border-green-400 bg-green-50' : ''}`}
          >
            <div className="text-center">
              <Upload className={`w-8 h-8 mx-auto mb-2 ${fileName ? 'text-green-600' : 'text-slate-400 group-hover:text-blue-600'} transition-colors duration-200`} />
              <div className={`font-semibold ${fileName ? 'text-green-700' : 'text-slate-600'}`}>
                {currentIsUploading ? 'Uploading...' : fileName ? fileName : 'Choose File'}
              </div>
              <div className="text-sm text-slate-500 mt-1">CSV or XLSX files only</div>
            </div>
          </label>
        </div>
        {currentUploadStatus && (
          <div className={`mt-3 p-3 rounded-lg text-sm font-medium ${
            currentUploadStatus.startsWith('Error') 
              ? 'bg-red-50 text-red-700 border border-red-200' 
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {currentUploadStatus}
          </div>
        )}
      </div>
    );
  };

  const getMeshingFields = () => {
    switch (formData.meshingMethod) {
      case 'Amount':
        return (
          <div className="grid md:grid-cols-2 gap-6">
            <InputField
              label="HORIZONTAL"
              value={formData.horizontal}
              onChange={(value) => handleInputChange('horizontal', value)}
              type="number"
            />
            <InputField
              label="VERTICAL"
              value={formData.vertical}
              onChange={(value) => handleInputChange('vertical', value)}
              type="number"
            />
          </div>
        );
      case 'Distance':
        return (
          <div className="grid md:grid-cols-2 gap-6">
            <InputField
              label="DISTANCE BETWEEN SENSORS"
              value={formData.distanceBetweenSensors}
              onChange={(value) => handleInputChange('distanceBetweenSensors', value)}
              type="number"
              step="0.1"
            />
            <InputField
              label="MINIMUM DISTANCE FROM SIDES"
              value={formData.minimumDistanceFromSides}
              onChange={(value) => handleInputChange('minimumDistanceFromSides', value)}
              type="number"
              step="0.1"
            />
          </div>
        );
      case 'Automatic':
        return (
          <>
            <InputField
              label="SQUARE METER PER SENSOR"
              value={formData.squareMeterPerSensor}
              onChange={(value) => handleInputChange('squareMeterPerSensor', value)}
              type="number"
              step="0.1"
            />
            <InputField
              label="IMPORTANCE OF KEEPING RATIO"
              value={formData.importanceOfKeepingRatio}
              onChange={(value) => handleInputChange('importanceOfKeepingRatio', value)}
              type="number"
              step="0.01"
              placeholder="from 0 to 1, default=0.51"
            />
            <div className="text-sm text-slate-600 mb-4">
              <div>closer to 0: a mesh with fewer sensors.</div>
              <div>closer to 1: a mesh with a shape similar to surface.</div>
            </div>
            <InputField
              label="MIN DISTANCE FROM SIDES (% OF AREA)"
              value={formData.minDistanceFromSidesPercent}
              onChange={(value) => handleInputChange('minDistanceFromSidesPercent', value)}
              type="number"
              placeholder="from 0 to 100, default=0"
            />
            <InputField
              label="MESHING DETAIL (BEFORE SAMPLING)"
              value={formData.meshingDetail}
              onChange={(value) => handleInputChange('meshingDetail', value)}
              type="number"
              placeholder="10, 15, 20, default=10"
            />
          </>
        );
      default:
        return null;
    }
  };

  // Loading state
  if (isLoadingProject) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading project information...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
              <Zap className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
              Raytracing Configuration
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Configure advanced raytracing parameters for{' '}
              <span className="font-bold text-blue-600">{projectName || projectId}</span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-8">
            <button 
              onClick={() => navigate(`/project-processing/${projectId}`)}
              className="group bg-white hover:bg-slate-50 text-slate-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 shadow-md hover:shadow-lg border border-slate-200"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
              BACK TO MENU
            </button>
            
            <button 
              onClick={refetch}
              disabled={isLoadingConfigs}
              className="group bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${isLoadingConfigs ? 'animate-spin' : ''}`} />
              REFRESH
            </button>

            <button 
              onClick={createNewConfiguration}
              className="group bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 shadow-md hover:shadow-lg"
            >
              <Settings className="w-5 h-5" />
              NEW CONFIG
            </button>

            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="group bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'SAVING...' : 'SAVE CONFIG'}
            </button>

            {selectedConfigId && (
              <button 
                onClick={handleDelete}
                className="group bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 shadow-md hover:shadow-lg"
              >
                <Trash2 className="w-5 h-5" />
                DELETE
              </button>
            )}
          </div>

          {/* Configuration Status */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-700">Configuration Status</h3>
              {isLoadingConfigs && <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />}
            </div>
            
            {configs.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-slate-500 mb-2">No configurations found for this project</div>
                <div className="text-sm text-slate-400">Create a new configuration to get started</div>
              </div>
            ) : (
              <div>
                <div className="text-sm text-slate-600 mb-4">
                  Found {configs.length} configuration{configs.length !== 1 ? 's' : ''} for this project
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {configs.map(config => (
                    <button
                      key={config.id}
                      onClick={() => loadConfiguration(config)}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                        selectedConfigId === config.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className="font-semibold text-slate-700">Config #{config.id}</div>
                      <div className="text-sm text-slate-500">Method: {config.meshing_method}</div>
                      <div className="text-sm text-slate-500">Skylines: {config.number_of_skylines}</div>
                      <div className="text-sm text-slate-500">Interval: {config.processing_interval}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* PRE RAY TRACING Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
                <div className="flex items-center gap-3">
                  <Settings className="w-6 h-6 text-white" />
                  <h2 className="text-2xl font-bold text-white">PRE RAY TRACING</h2>
                </div>
                <p className="text-blue-100 mt-2">Setup meshing and clustering parameters</p>
              </div>
              
              <div className="p-8">
                <SelectField
                  label="MESHING METHOD"
                  value={formData.meshingMethod}
                  onChange={(value) => handleInputChange('meshingMethod', value)}
                  options={['Amount', 'Distance', 'Automatic']}
                />

                <InputField
                  label="PROCESSING INTERVAL"
                  value={formData.processingInterval}
                  onChange={(value) => handleInputChange('processingInterval', value)}
                  type="number"
                />

                {getMeshingFields()}

                <InputField
                  label="SENSOR OFFSET"
                  value={formData.sensorOffset}
                  onChange={(value) => handleInputChange('sensorOffset', value)}
                  type="number"
                  step="0.01"
                />

                <SelectField
                  label="SURFACE ORIENTATION"
                  value={formData.surfaceOrientation}
                  onChange={(value) => handleInputChange('surfaceOrientation', value)}
                  options={['Yes', 'No']}
                />

                {/* Window Schedule File Upload with Required Notice */}
                {formData.surfaceOrientation === 'Yes' && (
                  <div className="mb-6">
                    {/* Required indicator */}
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-800 font-medium">
                          <span className="font-bold text-red-600">*Required:</span> Window Schedule File is mandatory when Surface Orientation is enabled
                        </span>
                      </div>
                    </div>
                    
                    <FileUploadField
                      label="Window Schedule File *"
                      fileName={formData.windowScheduleFile}
                      onFileChange={handleFileUpload}
                      fileType="window"
                    />
                  </div>
                )}

                <SelectField
                  label="ROOM WINDOW CLUSTERING"
                  value={formData.roomWindowClustering}
                  onChange={(value) => handleInputChange('roomWindowClustering', value)}
                  options={['No', 'Yes']}
                />

                {/* Room Data File Upload */}
                {formData.roomWindowClustering === 'Yes' && (
                  <FileUploadField
                    label="Room Data File"
                    fileName={formData.roomDataFile}
                    onFileChange={handleFileUpload}
                    fileType="room"
                  />
                )}

                <SelectField
                  label="HIGHER LEVEL CLUSTERING"
                  value={formData.higherLevelClustering}
                  onChange={(value) => handleInputChange('higherLevelClustering', value)}
                  options={['No', 'Yes']}
                />

                {/* Cluster Hierarchy */}
                {formData.higherLevelClustering === 'Yes' && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider flex items-center gap-2">
                      <Settings className="w-4 h-4 text-blue-600" />
                      CLUSTER HIERARCHY
                    </h3>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <div className="text-sm text-amber-800 font-medium flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">⚠️</span>
                        Note: Not all combinations are logical for the algorithm!
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 p-6 bg-slate-50 rounded-xl border border-slate-200">
                      {[1, 2, 3, 4, 5].map(level => (
                        <div key={level} className="space-y-2">
                          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                            Level {level}
                          </label>
                          <div className="relative">
                            <select
                              value={formData[`clusterLevel${level}`]}
                              onChange={(e) => handleInputChange(`clusterLevel${level}`, e.target.value)}
                              className="w-full p-3 border-2 border-slate-200 rounded-lg appearance-none bg-white hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 text-sm font-medium"
                            >
                              {['WINDOW', 'ROOM', 'FLOOR', 'BUILDING', 'FACADE', 'NONE'].map(option => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional fields for Amount and Distance methods */}
                {(formData.meshingMethod === 'Amount' || formData.meshingMethod === 'Distance') && (
                  <>
                    <div className="grid md:grid-cols-2 gap-6">
                      <InputField
                        label="SQUARE METER PER SENSOR"
                        value={formData.squareMeterPerSensor}
                        onChange={(value) => handleInputChange('squareMeterPerSensor', value)}
                        type="number"
                        step="0.1"
                      />

                      <InputField
                        label="ALPHA"
                        value={formData.alpha}
                        onChange={(value) => handleInputChange('alpha', value)}
                        type="number"
                        step="0.01"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <InputField
                        label="MARGIN PERCENT"
                        value={formData.marginPercent}
                        onChange={(value) => handleInputChange('marginPercent', value)}
                        type="number"
                        step="0.1"
                      />

                      <InputField
                        label="OPTIMAL CUBE LENGTH"
                        value={formData.optimalCubeLength}
                        onChange={(value) => handleInputChange('optimalCubeLength', value)}
                        type="number"
                        step="0.1"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* RAY TRACING Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                <div className="flex items-center gap-3">
                  <Eye className="w-6 h-6 text-white" />
                  <h2 className="text-2xl font-bold text-white">RAY TRACING</h2>
                </div>
                <p className="text-indigo-100 mt-2">Configure rendering and analysis options</p>
              </div>
              
              <div className="p-8">
                <SelectField
                  label="CONSIDER TERRAIN"
                  value={formData.considerTerrain}
                  onChange={(value) => handleInputChange('considerTerrain', value)}
                  options={['Yes', 'No']}
                />

                <SelectField
                  label="CONSIDER VEGETATION"
                  value={formData.considerVegetation}
                  onChange={(value) => handleInputChange('considerVegetation', value)}
                  options={['Yes', 'No']}
                />

                <InputField
                  label="NUMBER OF SKYLINES"
                  value={formData.numberOfSkylines}
                  onChange={(value) => handleInputChange('numberOfSkylines', value)}
                  type="number"
                />

                <div className="grid md:grid-cols-2 gap-6">
                  <SelectField
                    label="PLOT SKYLINE"
                    value={formData.plotSkyline}
                    onChange={(value) => handleInputChange('plotSkyline', value)}
                    options={['Yes', 'No']}
                  />

                  <SelectField
                    label="STORE SKYLINE"
                    value={formData.storeSkyline}
                    onChange={(value) => handleInputChange('storeSkyline', value)}
                    options={['Yes', 'No']}
                  />
                </div>

                {/* Current Configuration Info */}
                <div className="mt-8 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-700 mb-2">Current Configuration</h4>
                  <div className="text-sm text-slate-600 space-y-1">
                    {selectedConfigId ? (
                      <>
                        <div><span className="font-medium">Config ID:</span> {selectedConfigId}</div>
                        <div><span className="font-medium">Project:</span> {projectName || projectId}</div>
                        <div><span className="font-medium">Method:</span> {formData.meshingMethod}</div>
                        <div><span className="font-medium">Cluster Level:</span> {calculateClusterLevel()}</div>
                        <div><span className="font-medium">Room Clustering:</span> {formData.roomWindowClustering}</div>
                        <div><span className="font-medium">Surface Orientation:</span> {formData.surfaceOrientation}</div>
                        <div><span className="font-medium">Higher Level Clustering:</span> {formData.higherLevelClustering}</div>
                      </>
                    ) : (
                      <div className="text-amber-600">Creating new configuration...</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-12 text-center">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-12 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
            >
              <span className="flex items-center gap-3">
                {isSaving ? 'Saving Configuration...' : selectedConfigId ? 'Update Configuration' : 'Create Configuration'}
                <ChevronDown className="w-5 h-5 rotate-[-90deg] group-hover:translate-x-1 transition-transform duration-200" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default RaytracingConfiguration;
